import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { updateReport } from 'api/reports';
import { useFetchQueryParams } from 'assets/js/pages/reports/useFetchQueryParams';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface UpdateReportDialog extends AlertDialog.AlertDialogRootProps {}

const UpdateReportDialog = (props: UpdateReportDialog) => {
    const { children, ...rest } = props;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { success, error } = useToast();
    const { data: report } = useCurrentReport();
    const params = useFetchQueryParams();

    const { isPending, mutate: update } = useMutation({
        mutationFn: () =>
            updateReport(accountId, report.id, {
                query: {
                    columns: params?.columns,
                    filters: params?.filters,
                    graph_settings: params?.graph_settings,
                    display_settings: params?.display_settings,
                    date_range: params?.date_range,
                },
            }),
        onSuccess: () => {
            success('Saved report successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reports(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to save report');
        },
    });

    return (
        <AlertDialog.Root {...rest} type={'info'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Save report</AlertDialog.Title>
                        <AlertDialog.Description>
                            Saving this report will apply all added or removed filters and update
                            any existing scheduled reports.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action disabled={isPending} onClick={() => update()}>
                            Save
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const CustomDateRangeDialogTrigger = AlertDialog.Trigger;

const Root = UpdateReportDialog;
const Trigger = CustomDateRangeDialogTrigger;

export {
    UpdateReportDialog,
    CustomDateRangeDialogTrigger,
    //
    Root,
    Trigger,
};
